import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const RecordingContext = createContext();

export const useRecording = () => useContext(RecordingContext);

export const RecordingProvider = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [originPath, setOriginPath] = useState(null);

  useEffect(() => {
    let timer;
    if (isRecording && recordingStartTime) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => clearInterval(timer);
  }, [isRecording, recordingStartTime]);

  const startRecording = async (origin = null) => {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const mimeType = isIOS ? undefined : 'audio/webm';

      let recorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (err) {
        console.error('MediaRecorder creation failed:', err);
        alert('Recording not supported on this device or browser.');
        return;
      }

      recordedChunksRef.current = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecordingStartTime(Date.now());
      setIsRecording(true);
      if (origin) setOriginPath(origin);
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  const stopRecording = () => {
    if (!isRecording || !mediaRecorderRef.current) return Promise.reject('No recording in progress');

    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;

      recorder.onstop = () => {
        const mimeType = recordedChunksRef.current?.[0]?.type || 'audio/webm';
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);

        if (recorder.stream) {
          recorder.stream.getTracks().forEach(track => track.stop());
        }

        setIsRecording(false);
        setRecordingStartTime(null);
        recordedChunksRef.current = [];
        resolve({ blob, url });
      };

      recorder.stop();
    });
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingStartTime(null);
    recordedChunksRef.current = [];
  };

  return (
    <RecordingContext.Provider value={{
      isRecording,
      startRecording,
      stopRecording,
      cancelRecording,
      recordingStartTime,
      elapsedTime,
      originPath,
      setOriginPath,
    }}>
      {children}
    </RecordingContext.Provider>
  );
};
